import { isNotDefined } from '@sgme/fp';
import { Select } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import deepEqual from 'fast-deep-equal';
import { useCallback } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { userApi } from '@/api/user/index.ts';
import { useCheckCashByTradeCapture } from '@/App/tiles/CashTile/CashContext.tsx';
import { ForwardSwitch } from '@/App/tiles/CashTile/ForwardTile/ForwardSwitch.tsx';
import { FormattedFXDate } from '@/components/ui/FormattedFXDate.tsx';
import { getConfig } from '@/config/config.ts';
import { selectCashTile } from '@/store/cash/selectors/tile.ts';
import { useAppSelector } from '@/store/hooks.ts';

const NDF_LOCAL_MARKET_CURRENCIES = getConfig().app?.ndfLocalMarketCurrencies;

type NdfFieldsProps = {
  tileId: string;
};

/**
 * 3 cases:
 *
 * | Currency Pair | Settlement Ccy | Cross Ccy | Reference** | Reference Ccy 1 | Reference Ccy 2*** |
 * | ------------- | -------------- | --------- | ----------- | --------------- | ------------------ |
 * | EUR/USD       | x              | -         | x           | -               | -                  |
 * | EUR/GBP       | x              | -         | x           | -               | -                  |
 * | ------------- | -------------- | --------- | ----------- | --------------- | ------------------ |
 * | USD/BRL*      | -              | -         | x           | -               | -                  |
 * | USD/CNY       | -              | -         | x           | -               | -                  |
 * | ------------- | -------------- | --------- | ----------- | --------------- | ------------------ |
 * | EUR/CNY       | -              | x         | -           | x               | x                  |
 * | EUR/BRL*      | -              | x         | -           | x               | x                  |
 * * BRL is a local market currency for the user, so it can be delivered
 * ** Reference = Reference Ccy 1 = possible fixing source
 * *** Reference Ccy 2 = snd fixing source
 *
 * see: fx-web/app/src/components/FxCashTile/ForwardFixing/ForwardFixing.tsx
 *
 * Visibility conditions:
 * - Settlement ccy: if possible fixing currencies is defined and has at least 1 item
 * - Cross ccy: if possible X currencies is defined and has at least 1 item
 * - Reference: if no cross ccy (!isCrossed) and possible fixing sources is defined and has at least 1 item
 * - Reference Ccy 1: if cross ccy (isCrossed) and possible fixing sources is defined and has at least 1 item
 * - Reference Ccy 2: if possible snd fixing sources is defined and has at least 1 item
 */
export const NdfFields = (props: NdfFieldsProps) => {
  const { tileId } = props;

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const checkCashByTradeCapture = useCheckCashByTradeCapture();

  const {
    currency1,
    currency2,
    isLocalMarket,
    onshoreSettlementCcy,
    offshoreSettlementCcy,
    isCrossed,
    xCurrency,
    possibleXCurrencies,
    fixingCurrency,
    possibleFixingCurrencies,
    fixingDate,
    fixingSource,
    fixingSourceDefault,
    possibleFixingSources,
    sndFixingSource,
    sndFixingSourceDefault,
    possibleSndFixingSources,
    tradeCapture,
  } = useAppSelector((state) => {
    const tile = selectCashTile(state, tileId);

    const localMarketsCcy = userApi.endpoints.currentUser.select()(state).data?.localMarketsCcy ?? [];

    if (isNotDefined(tile) || tile.type !== 'FxNdf') {
      return {
        currency1: '',
        currency2: '',
        isLocalMarket: false,
        onshoreSettlementCcy: '',
        offshoreSettlementCcy: '',
        isCrossed: false,
        xCurrency: '',
        possibleXCurrencies: [],
        fixingCurrency: '',
        possibleFixingCurrencies: [],
        fixingDate: '',
        fixingSource: '',
        fixingSourceDefault: '',
        possibleFixingSources: [],
        sndFixingSource: '',
        sndFixingSourceDefault: '',
        possibleSndFixingSources: [],
        tradeCapture: undefined,
      };
    }

    const currency1 = tile.properties.currency1 ?? '';
    const currency2 = tile.properties.currency2 ?? '';
    const isCcy1LocalMarket = NDF_LOCAL_MARKET_CURRENCIES?.includes(currency1) && localMarketsCcy?.includes(currency1);
    const isCcy2LocalMarket = NDF_LOCAL_MARKET_CURRENCIES?.includes(currency2) && localMarketsCcy?.includes(currency2);
    // onshore = settle in local currency (e.g. BRL), offshore = settle in non-local currency (e.g. USD)
    const onshoreSettlementCcy = isCcy1LocalMarket ? currency1 : currency2;
    const offshoreSettlementCcy = isCcy1LocalMarket ? currency2 : currency1;
    const isLocalMarket = isCcy1LocalMarket || isCcy2LocalMarket;

    return {
      currency1,
      currency2,
      isLocalMarket,
      onshoreSettlementCcy,
      offshoreSettlementCcy,

      isCrossed: tile.properties.isCrossed,

      xCurrency: tile.properties.xCurrency,

      possibleXCurrencies: tile.properties.possibleXCurrencies?.possible ?? [],

      fixingCurrency: tile.properties.fixingCurrency,

      possibleFixingCurrencies: tile.properties.possibleFixingCurrencies?.possible ?? [],

      fixingDate: tile.properties.fixingDate,

      fixingSource: tile.properties.fixingSource,
      fixingSourceDefault:
        tile.properties.fixingSourceOverriddenDefault ?? tile.properties.possibleFixingSources?.default,

      possibleFixingSources: tile.properties.possibleFixingSources?.possible ?? [],

      sndFixingSource: tile.properties.sndFixingSource,
      sndFixingSourceDefault:
        tile.properties.sndFixingSourceOverriddenDefault ?? tile.properties.possibleSndFixingSources?.default,

      possibleSndFixingSources: tile.properties.possibleSndFixingSources?.possible ?? [],

      tradeCapture: tile.tradeCapture,
    };
  }, deepEqual);

  const handleFixingCurrencyChange = useCallback(
    (currency: string) => {
      if (isNotDefined(tradeCapture) || isNotDefined(currency) || fixingCurrency === currency) {
        return;
      }

      sgwtWebAnalytics?.trackEvent('Cash', 'Fixing currency changed', currency);

      checkCashByTradeCapture({
        fixingCurrency: currency,
      });
    },
    [tradeCapture, fixingCurrency, checkCashByTradeCapture, sgwtWebAnalytics],
  );

  const handleXCurrencyChange = useCallback(
    (currency: string) => {
      if (isNotDefined(tradeCapture) || isNotDefined(currency) || xCurrency === currency) {
        return;
      }

      sgwtWebAnalytics?.trackEvent('Cash', 'X currency changed', currency);

      checkCashByTradeCapture({
        xCurrency: currency,
      });
    },
    [tradeCapture, xCurrency, checkCashByTradeCapture, sgwtWebAnalytics],
  );

  const handleFixingSourceChange = useCallback(
    (source: string) => {
      if (isNotDefined(tradeCapture) || isNotDefined(source) || fixingSource === source) {
        return;
      }

      sgwtWebAnalytics?.trackEvent('Cash', 'Fixing source changed', source);

      checkCashByTradeCapture({
        fixingSource: source,
      });
    },
    [tradeCapture, fixingSource, checkCashByTradeCapture, sgwtWebAnalytics],
  );

  const marketType = fixingCurrency === onshoreSettlementCcy ? 'Onshore' : 'Offshore';

  const handleMarketTypeChange = useCallback(
    (value: 'Onshore' | 'Offshore') => {
      if (isNotDefined(tradeCapture)) {
        return;
      }

      const settlementCurrency = value === 'Onshore' ? onshoreSettlementCcy : offshoreSettlementCcy;

      sgwtWebAnalytics?.trackEvent('Cash', 'Market type changed', value);

      checkCashByTradeCapture({ fixingCurrency: settlementCurrency });
    },
    [tradeCapture, onshoreSettlementCcy, offshoreSettlementCcy, checkCashByTradeCapture, sgwtWebAnalytics],
  );

  const handleFixingSndSourceChange = (source: string) => {
    if (isNotDefined(tradeCapture) || isNotDefined(source) || sndFixingSource === source) {
      return;
    }

    sgwtWebAnalytics?.trackEvent('Cash', 'Fixing snd source changed', source);

    checkCashByTradeCapture({
      sndFixingSource: source,
    });
  };

  return (
    <div
      className="px-2 mt-16px fs-12"
      style={{
        display: 'grid',
        gap: '5px',
        gridTemplateColumns: '100px 151px 20px',
        gridTemplateRows: '20px',
        alignItems: 'center',
      }}
    >
      {/*Cross currency*/}
      {isCrossed && (
        <>
          <div className="text-end text-secondary">Cross currency</div>
          <div>
            <Select
              selectedItem={xCurrency}
              itemsAsObjects={possibleXCurrencies}
              onChange={handleXCurrencyChange}
              className="form-select form-select-sm"
              data-e2e="cross-currency"
            />
          </div>
          <div />
        </>
      )}

      {/*Market type (local market: onshore/offshore)*/}
      {!isCrossed && isLocalMarket && (
        <>
          <div className="text-end text-secondary">Market type</div>
          <ForwardSwitch
            name="marketType"
            identifier="marketType"
            values={['Onshore', 'Offshore'] as const}
            selectedValue={marketType}
            onChange={handleMarketTypeChange}
            data-e2e="market-type"
          />
          <div />
        </>
      )}

      {/*Settlement ccy (non-local market)*/}
      {!isCrossed && !isLocalMarket && (
        <>
          <div className="text-end text-secondary">Settlement ccy</div>
          <div>
            <Select
              selectedItem={fixingCurrency}
              itemsAsObjects={possibleFixingCurrencies}
              onChange={handleFixingCurrencyChange}
              className="form-select form-select-sm"
              data-e2e="settlement-ccy"
            />
          </div>
          <div />
        </>
      )}

      {/*Ref. EUR/USD*/}
      <div className="text-end text-secondary">{isCrossed ? `Ref. ${currency1}/${xCurrency}` : 'Reference'}</div>
      <div>
        <Select
          selectedItem={fixingSource}
          itemsAsObjects={possibleFixingSources}
          onChange={handleFixingSourceChange}
          className="form-select form-select-sm"
          data-e2e={isCrossed ? 'first-fixing-source' : 'fixing-source'}
        />
      </div>
      <div>
        {fixingSourceDefault !== fixingSource && (
          <OverlayTrigger
            overlay={
              <Tooltip>
                <FormattedMessage
                  id="cash.ndf.fixingSource.toolip.notDefault"
                  values={{ defaultSource: fixingSourceDefault }}
                />
              </Tooltip>
            }
            placement="right"
          >
            <i className="icon icon-sm" data-e2e="second-fixing-source-info">
              info_outline
            </i>
          </OverlayTrigger>
        )}
      </div>

      {/*Ref. USD/CNY*/}
      {isCrossed && (
        <>
          <div className="text-end text-secondary">
            Ref. {xCurrency}/{currency2}
          </div>
          <div>
            <Select
              selectedItem={sndFixingSource}
              itemsAsObjects={possibleSndFixingSources}
              onChange={handleFixingSndSourceChange}
              className="form-select form-select-sm"
              data-e2e="second-fixing-source"
            />
          </div>
          <div>
            <OverlayTrigger
              overlay={
                <Tooltip>
                  {sndFixingSourceDefault === sndFixingSource ? (
                    <FormattedMessage id="cash.ndf.sndFixingSource.toolip.default" />
                  ) : (
                    <FormattedMessage
                      id="cash.ndf.sndFixingSource.toolip.notDefault"
                      values={{ defaultSource: sndFixingSourceDefault }}
                    />
                  )}
                </Tooltip>
              }
              placement="right"
            >
              <i className="icon icon-sm" data-e2e="second-fixing-source-info">
                info_outline
              </i>
            </OverlayTrigger>
          </div>
        </>
      )}

      {/*Near fixing date*/}
      <div className="text-end text-secondary">Fixing date</div>
      <div data-e2e="fixing-date">
        <FormattedFXDate value={fixingDate} dateStyle="medium" />
      </div>
      <div />
    </div>
  );
};
